import type { AdminRpcMethods } from '@app/lib'
import { useCallback, useMemo, useState } from 'react'
import { type RpcCallOptions, useRpc } from './use-rpc'

export type AdminRpcMethodName = keyof AdminRpcMethods & string
export type AdminRpcInput<K extends AdminRpcMethodName> = AdminRpcMethods[K]['input']
export type AdminRpcOutput<K extends AdminRpcMethodName> = AdminRpcMethods[K]['output']

export type AdminRpcCaller = <K extends AdminRpcMethodName>(
	method: K,
	input: AdminRpcInput<K>,
	options?: RpcCallOptions,
) => Promise<AdminRpcOutput<K>>

/**
 * Typed wrapper over /rpc bound to the shared AdminRpcMethods contract.
 */
export function useAdminRpc(): AdminRpcCaller {
	const rpc = useRpc<AdminRpcMethods>()
	return useMemo<AdminRpcCaller>(
		() => (method, input, options) => rpc(method, input as never, options) as never,
		[rpc],
	)
}

export type WorkflowActionState = {
	isPending: boolean
	error: string | null
}

export function useWorkflowAction<K extends AdminRpcMethodName>(
	method: K,
	options?: {
		onSuccess?: (output: AdminRpcOutput<K>) => void | Promise<void>
		onError?: (error: Error) => void
	},
): {
	run: (input: AdminRpcInput<K>) => Promise<AdminRpcOutput<K> | null>
	state: WorkflowActionState
} {
	const call = useAdminRpc()
	const [state, setState] = useState<WorkflowActionState>({ isPending: false, error: null })

	const run = useCallback(async (input: AdminRpcInput<K>): Promise<AdminRpcOutput<K> | null> => {
		setState({ isPending: true, error: null })
		try {
			const output = await call(method, input)
			await options?.onSuccess?.(output)
			setState({ isPending: false, error: null })
			return output
		} catch (err) {
			const error = err instanceof Error ? err : new Error('Unknown error')
			setState({ isPending: false, error: error.message })
			options?.onError?.(error)
			return null
		}
	}, [call, method, options])

	return { run, state }
}
